import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useState } from "react";

import type { SurveyDetailsV2Dto, SurveyDto } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import { AlertCircleIcon } from "@/components/Icons/AlertCircleIcon";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { RadioCard } from "@/components/Radio/RadioCard";
import { Button } from "@/design-system/Button";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSurveyQueries } from "@/queries/surveys/queryOptions";
import { useTranslation } from "@/translations";

type SurveyExportScope = "allProjects" | "currentProject";

type SurveyExportModalProps = ModalBaseProps & {
  survey: SurveyDetailsV2Dto | SurveyDto;
};

export function SurveyExportModal({
  isOpened,
  onOpenChange,
  onExitComplete,
  survey,
}: SurveyExportModalProps): React.ReactNode {
  const isBulkSurvey = Boolean(survey.bulkSurveyId);
  const [selectedExportScope, setSelectedExportScope] = useState<SurveyExportScope>(
    isBulkSurvey ? "allProjects" : "currentProject",
  );
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const showFlashToast = useFlashToast();
  const surveyQueries = useSurveyQueries();
  const {
    isFetching,
    error,
    refetch: fetchData,
  } = useQuery({
    ...surveyQueries.export({
      surveyId: survey.id,
      query: { includeBulkResults: selectedExportScope === "allProjects" },
    }),
    enabled: false,
  });

  const handleDownload = async () => {
    const { data, isSuccess } = await fetchData();

    if (!isSuccess || !data) {
      showFlashToast({
        message: t("page.surveys.export.modal.notification.error"),
        variant: "warning",
      });

      return;
    }

    const newBlob = new Blob([data]);
    const blobUrl = window.URL.createObjectURL(newBlob);
    const link = document.createElement("a");
    const fileName =
      selectedExportScope === "allProjects"
        ? `${survey.title.replace(/[^a-zA-Z0-9]/g, "")}_all-projects_${format(new Date(), "yyyy-MM-dd")}.xlsx`
        : `${survey.title.replace(/[^a-zA-Z0-9]/g, "")}_${sessionUser.project.name}_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
    link.href = blobUrl;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }

    window.URL.revokeObjectURL(blobUrl);
    showFlashToast({
      message: t("page.surveys.export.modal.notification.success"),
      variant: "success",
    });
    onOpenChange(false);
  };

  return (
    <Modal.Root
      size="sm"
      title={t("page.surveys.export.modal.title")}
      onExitComplete={() => {
        onExitComplete?.();
        setSelectedExportScope(isBulkSurvey ? "allProjects" : "currentProject");
      }}
      data-testid="survey-export-modal"
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        <Stack gap="md">
          {isBulkSurvey ? (
            <>
              <p>{t("page.surveys.export.modal.bulk-survey-description")}</p>
              <FormRadioCardGroup layout="vertical">
                <RadioCard
                  value="allProjects"
                  label={t("page.surveys.export.modal.form.export-options.all-projects.label")}
                  name="scope"
                  checked={selectedExportScope === "allProjects"}
                  onChange={() => setSelectedExportScope("allProjects")}
                  data-testid="export-all-projects-option"
                  disabled={isFetching}
                />
                <RadioCard
                  value="currentProject"
                  label={t("page.surveys.export.modal.form.export-options.current-project.label", {
                    projectName: sessionUser.project.name,
                  })}
                  name="scope"
                  checked={selectedExportScope === "currentProject"}
                  onChange={() => setSelectedExportScope("currentProject")}
                  data-testid="export-current-project-option"
                  disabled={isFetching}
                />
              </FormRadioCardGroup>
            </>
          ) : (
            <p>{t("page.surveys.export.modal.single-survey-description")}</p>
          )}
          {error && (
            <aside className="w-fit">
              <Notice icon={<AlertCircleIcon />} variant="danger" message={t("page.surveys.export.modal.error")} />
            </aside>
          )}
        </Stack>
      </Modal.Body>
      <Modal.Actions>
        <Button isLoading={isFetching} disabled={!!error} onClick={handleDownload} data-testid="confirm-btn">
          {t("common.action.export")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
